import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@mui/material";
import { useTranslations } from "use-intl";
import clsx from "clsx";
import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import { useTravellersSelectionContext } from "@/app/lib/context/TravellersSelectionContext";
import BottomDrawer from "@/app/ui/flight-purchase/BottomDrawer";
import TravellersList from "@/app/ui/travellers/TravellersList";
import { FlightTrackingEvents, rudderAnalytics } from "@/app/lib/rudderstack";
import { type Traveller, type TravellerAgeGroup } from "@/app/lib/types/types";
import {
  Feature,
  isFeatureDisabled,
  isFeatureEnabled,
} from "@/app/lib/utils/featureFlags";
import { AdditionalServicesSelectionDrawer } from "@/app/ui/flight-purchase/AdditionalServicesSelection/AdditionalServicesSelectionDrawer";
import {
  getAvailableServices,
  getCheapestMandatoryServices,
} from "@/app/lib/utils/additionalServices";
import { travelerTypeToTypeCode } from "@/app/lib/utils/transformTraveler";
import { TravellersSelection } from "@/app/ui/travellers/TravellersSelection";
import { SwipeableDrawer } from "@/app/ui/SwipeableDrawer";
import { DrawerTopBar } from "@/app/ui/Drawer";
import { TravellerSelectionStatusDrawer } from "@/app/ui/travellers/TravellerSelectionStatusDrawer";
import { getTravellerTypeAtTravelDate } from "@/app/lib/utils/ageValidation";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";
import { flightAvailabilityQueryOptions } from "@/app/lib/hooks/useFlightAvailability";
import { useFlightSelectionPayloadContext } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";

export const Route = createFileRoute(
  "/_authenticated/_flights/_flight-offer/flights/offer/$id/step/2",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const { searchData } = useFlightSearchDataContext();
  const { getTravellerServices, setTravellerServices, selectedFlightPayload } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));

  const {
    travellerSlots,
    selectedTravellers,
    selectTraveller,
    getTravellerIndex,
  } = useTravellersSelectionContext();

  const { data: travellers } = useQuery(travellersQueryOptions());
  const [additionalServicesTraveller, setAdditionalServicesTraveller] =
    useState<Traveller | null>(null);
  const isAdditionalServicesDrawerOpen = !!additionalServicesTraveller;

  const getServicesInfo = useCallback(
    (traveller: Traveller) => {
      const availableServices = getAvailableServices({
        traveller,
        travellerIndex: getTravellerIndex(traveller.id),
        optionalServices: singleFlightDetails?.optionalServices,
        mandatoryServices: singleFlightDetails?.mandatoryServices,
      });

      const selectedServices = getTravellerServices(traveller.id);

      const availableMandatoryServiceCount = availableServices.filter(
        (service) => service.mandatory,
      ).length;

      // null if there are no mandatory services to select from, boolean otherwise
      const hasSelectedAllMandatoryServices =
        availableMandatoryServiceCount > 0
          ? selectedServices.filter((service) => service.mandatory).length ===
            availableMandatoryServiceCount
          : null;

      return {
        availableServices,
        hasSelectedAllMandatoryServices,
      };
    },
    [
      getTravellerIndex,
      getTravellerServices,
      singleFlightDetails?.mandatoryServices,
      singleFlightDetails?.optionalServices,
    ],
  );

  const hasSelectedMandatoryServicesForAllTravellers = useMemo(() => {
    if (isFeatureDisabled(Feature.AdditionalServiceSelection)) {
      return true;
    }

    return selectedTravellers.every((traveller) => {
      const servicesInfo = getServicesInfo(traveller);

      // Either all mandatory services are selected, or there are no mandatory services to select from
      return (
        servicesInfo.hasSelectedAllMandatoryServices === null ||
        servicesInfo.hasSelectedAllMandatoryServices === true
      );
    });
  }, [selectedTravellers, getServicesInfo]);

  const travelDate = new Date(searchData!.dates.departure);

  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  return (
    <>
      <div className="px-4">
        <TravellersSelection
          onSlotClick={(ageGroup, index) => {
            setSelectedSlot({ ageGroup, index });
          }}
          renderAdditionalTravellerControls={(traveller) => {
            if (isFeatureDisabled(Feature.AdditionalServiceSelection)) {
              return null;
            }

            const { availableServices, hasSelectedAllMandatoryServices } =
              getServicesInfo(traveller);

            if (availableServices.length === 0) {
              return null;
            }

            return (
              <div className="px-3">
                {hasSelectedAllMandatoryServices !== null ? (
                  <div
                    className={clsx("mt-1 mb-1 ps-7 text-xs", {
                      "text-semantic-error-strong":
                        !hasSelectedAllMandatoryServices,
                      "text-core-accent-500": hasSelectedAllMandatoryServices,
                    })}
                  >
                    {hasSelectedAllMandatoryServices
                      ? t("flights.additionalServicesSelected")
                      : t("flights.additionalServicesRequired")}
                  </div>
                ) : null}

                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  fullWidth
                  onClick={() => {
                    setAdditionalServicesTraveller(traveller);
                  }}
                  sx={{
                    marginTop: "calc(var(--spacing) * 3)",
                  }}
                >
                  {t("travellers.addAdditionalService")}
                </Button>
              </div>
            );
          }}
        />

        <SwipeableDrawer
          hasFullHeight
          heightOffset={24}
          open={!!selectedSlot}
          onClose={() => setSelectedSlot(null)}
          backgroundColor="var(--color-surface-background)"
        >
          <DrawerTopBar
            onCloseClick={() => setSelectedSlot(null)}
            title={
              selectedSlot
                ? t("travellers.addFor", {
                    type: t("travellers.travellerTypeN", {
                      type: selectedSlot.ageGroup,
                      number: selectedSlot.index + 1,
                    }),
                  })
                : null
            }
          />
          {selectedSlot ? (
            <TravellersList
              travellers={(travellers || []).filter(
                (traveller) =>
                  travelerTypeToTypeCode(
                    getTravellerTypeAtTravelDate(
                      traveller.birthDate,
                      travelDate,
                    ),
                  ) === selectedSlot.ageGroup,
              )}
              travelDate={travelDate}
              selectedTravellerId={
                travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
              }
              isSelectionDisabled={(traveller) =>
                selectedTravellers.some((t) => t.id === traveller.id)
              }
              onTravellerSelected={(travellerId) => {
                selectTraveller(selectedSlot.index, travellerId);
                setSelectedSlot(null);
              }}
            />
          ) : null}
        </SwipeableDrawer>
      </div>

      {isFeatureEnabled(Feature.AdditionalServiceSelection) &&
      isAdditionalServicesDrawerOpen ? (
        <AdditionalServicesSelectionDrawer
          open
          onClose={() => setAdditionalServicesTraveller(null)}
          onConfirm={(travellerId, selectedServices) => {
            setTravellerServices(travellerId, selectedServices);
            setAdditionalServicesTraveller(null);
          }}
          initialSelectedServices={getTravellerServices(
            additionalServicesTraveller.id,
          )}
          traveller={additionalServicesTraveller}
          travellerIndex={getTravellerIndex(additionalServicesTraveller.id)}
        />
      ) : null}

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <BottomDrawer
        buttonLabel={t("flights.continue")}
        onButtonClick={() => {
          rudderAnalytics.track(
            FlightTrackingEvents.USER_CONTINUED_ON_TRAVELERS,
            {
              passenger_count: selectedTravellers.length,
            },
          );

          // TEMP SOLUTION
          // preselect the cheapest mandatory service for each traveller
          selectedTravellers.forEach((traveller) => {
            const { availableServices } = getServicesInfo(traveller);

            const selectedServices =
              getCheapestMandatoryServices(availableServices);

            setTravellerServices(traveller.id, selectedServices);
          });

          navigate({ to: "/flights/offer/$id/step/3" });
        }}
        disabled={
          !allTravellersSelected ||
          !hasSelectedMandatoryServicesForAllTravellers ||
          isAdditionalDataLoading
        }
      />
    </>
  );
}
